<script setup lang="ts">
import { computed, onMounted, ref, unref, watch } from 'vue';
import { onBeforeRouteLeave, onBeforeRouteUpdate } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useForm } from 'vee-validate';
import * as yup from 'yup';

import { useProjectStore, useBlocksStore } from '@/stores';
import type {
  ProjectModuleDto,
  Locales,
  ProjectDto,
  ProjectModuleNewsItemConfigDto,
  ProjectModuleNewsItemConfigConfigDto,
} from '@/services/types/api.types';
import type { FileInfo } from '@/services/types/file.types';
import type { SavingOptions } from '@/services/types/file-uploader.types';
import { useLanguageValidationErrors } from '@/composables';
import { useWatchItemLoading } from '@/composables/watch-item-loading';
import { wrapSavingOperation } from '@/helpers/notifications.helper';
import { getLanguageEnabled, getTranslation, provideEditLocale } from '@/composables/data-display-utils';
import { useResolvedProjectThemeKey } from '@/composables/useResolvedProjectThemeKey';
import { formSaveImageGuard, formSaveNavigationGuard, hasRouteChanged, resetFormToItem } from '@/helpers/form.helper';
import { localizableString } from '@/helpers/validation.helper';
import { useNewsBreadcrumbOptions, useModuleBreadcrumbOptions } from '@/composables/breadcrumb-options';
import { useRouteNumberParam } from '@/composables/use-route-param';
import type { BreadcrumbElement } from '@/helpers/breadcrumbs.helper';
import { Constants } from '@/constants';
import type { BlockParent, BlockRouteFor } from '@/types/modules';

import FormActions from '@/components/FormActions.vue';
import DynamicTabs from '@/components/DynamicTabs.vue';
import TopPageNavigation from '@/components/TopPageNavigation.vue';
import { useNewsItemsStore } from '@/stores/newsItems.store';
import NewsItemInfo from '@/components/newsItem/NewsItemInfo.vue';

// PROPS
const { t } = useI18n();

// STORE
const projectStore = useProjectStore();
const blocksStore = useBlocksStore();
const newsItemsStore = useNewsItemsStore();

// CONST
const moduleId = useRouteNumberParam('moduleId');
const projectId = useRouteNumberParam('projectId');
// A news block is a project's: a page never holds one.
const blockParent = computed<BlockParent>(() => ({ type: Constants.Block.ParentType.Project, id: projectId.value }));
const newsItemId = useRouteNumberParam('id');

const handleSaveImage = async (options: SavingOptions) => {
  wrapSavingOperation(async () => await newsItemsStore.saveImage(newsItemId.value, options));
};

const removeNewsImage = async () => {
  await newsItemsStore.removeImage(newsItemId.value);
};

const handleClearImage = async () => {
  formSaveImageGuard(isDirty.value, removeNewsImage);
};

// REF
const openedTab = ref<string>('infos');
const dynamicTabsRef = ref<InstanceType<typeof DynamicTabs> | null>(null);
const languageEnabled = ref<Locales[]>([]);
const validationSchema = computed(() =>
  yup.object<ProjectModuleNewsItemConfigDto>({
    title: localizableString(languageEnabled.value),
    description: localizableString(languageEnabled.value),
    date: yup.string().required(),
    config: yup
      .object<ProjectModuleNewsItemConfigConfigDto>({
        image: yup.mixed<FileInfo>().optional().nullable(),
        content: localizableString(languageEnabled.value, { optional: true, nullable: true }).optional().nullable(),
      })
      .required(),
    status: yup.number().required(),
  }),
);
const formContext = useForm({ validationSchema });
const { handleSubmit, errors, meta, resetForm } = formContext;
const languageErrors = useLanguageValidationErrors(errors);

const module = useWatchItemLoading<ProjectModuleDto>(moduleId, blocksStore);

const newsItem = useWatchItemLoading<ProjectModuleNewsItemConfigDto>(newsItemId, newsItemsStore, (item) => {
  resetFormToItem(formContext, item);
});

const project = ref<ProjectDto | undefined>(undefined);
const resolvedThemeKey = useResolvedProjectThemeKey(project);

const editLocale = provideEditLocale(languageEnabled);

// COMPUTED
const isDirty = computed(() => {
  return unref(meta).dirty;
});

const tabs = computed(() => {
  return [
    { id: 'infos', name: t('project-module.tabs.infos'), isDirty: isDirty.value, icon: 'o_info' },
    // { id: 'contexts', name: t('project-module.tabs.contexts'), isDirty: false, icon: 'link' }
  ];
});

// This route is a news item's, so a block opens on the project block route rather than on this one.
const blockRouteFor: BlockRouteFor = (blockId) => ({
  name: 'module_details',
  params: { projectId: projectId.value, moduleId: blockId },
});
const { moduleOptions } = useModuleBreadcrumbOptions(blockParent, blockRouteFor);

const { newsItemsOptions } = useNewsBreadcrumbOptions(moduleId.value, projectId.value);

const onSubmit = handleSubmit(async (values) => {
  console.debug('submitting', values);
  wrapSavingOperation(
    async () => await newsItemsStore.saveNewsItem(values as ProjectModuleNewsItemConfigDto, projectId.value),
    () => resetForm({ values: newsItem.value! }),
  );
});

const onCancel = () => {
  if (newsItem) {
    resetForm({ values: newsItem! });
  } else {
    console.warn('onCancel: module is null');
  }
};

onMounted(async () => {
  await blocksStore.fetchModuleById(moduleId.value, blockParent.value);
  await projectStore.fetchById(projectId.value);
  project.value = projectStore.getItem(projectId.value);

  await newsItemsStore.fetchNewsListItemsByModuleId(moduleId.value);

  languageEnabled.value = getLanguageEnabled(project.value);
});

// ROUTER
onBeforeRouteLeave(formSaveNavigationGuard(meta));
onBeforeRouteUpdate((to, from, next) => {
  if (!hasRouteChanged(to, from)) {
    return formSaveNavigationGuard(meta)(to, from, next);
  }
  next();
});

const breadcrumbs = computed(() => {
  const breadcrumbItems: BreadcrumbElement[] = [
    {
      label: project.value?.title,
      to: { name: 'project_details', params: { id: projectId.value } },
      icon: 'o_folder_copy',
    },
    {
      label: module.value?.title,
      to: { name: 'module_details', params: { id: module.value?.id } },
      icon: 'o_folder',
      options: moduleOptions.value,
      activeId: module.value?.id,
    },
    {
      label: module.value?.title,
      to: {
        name: 'news_item_details',
        params: { id: module.value?.id, projectId: projectId.value, moduleId: module.value?.id },
      },
      icon: 'o_newspaper',
      options: newsItemsOptions.value,
      activeId: newsItemId.value,
    },
  ];

  return breadcrumbItems;
});

const preview = () => {
  const previewUrl = blocksStore.previewURL(moduleId.value);
  if (previewUrl) {
    window.open(previewUrl, '_blank')?.focus();
  }
};

function handleUpdate(tabId: string) {
  openedTab.value = tabId;
}

// LIFECYCLE
watch(
  moduleId,
  async (newModuleId) => {
    languageEnabled.value = [];
    if (Number.isNaN(newModuleId) || newModuleId < 0) {
      return;
    }
    const tabId = window.location.hash ? window.location.hash.substring(1) : 'infos';
    if (openedTab.value !== tabId) {
      openedTab.value = tabId;
      dynamicTabsRef.value?.setActiveTab(tabId);
    }
    await blocksStore.fetchModuleById(newModuleId, blockParent.value);
  },
  { immediate: true },
);

watch(
  module,
  (newModule) => {
    if (!newModule?.project_id) {
      project.value = undefined;
      languageEnabled.value = [];
      return;
    }
    project.value = projectStore.getItem(newModule.project_id);
    languageEnabled.value = project.value ? getLanguageEnabled(project.value) : [];
  },
  { immediate: true },
);

watch(
  newsItemId,
  async (newNewsItemId) => {
    if (Number.isNaN(newNewsItemId) || newNewsItemId < 0) {
      return;
    }

    await newsItemsStore.fetchNews(newNewsItemId);
  },
  { immediate: true },
);
</script>

<template>
  <q-page class="module-detail">
    <top-page-navigation
      :breadcrumbs="breadcrumbs"
      :language-enabled="languageEnabled"
      :to="{
        name: 'module_details',
        params: { projectId, moduleId },
        hash: '#news',
      }"
      @preview="preview"
    />
    <div class="title">
      <div>
        <q-icon name="newspaper" />
        <h1>{{ getTranslation(newsItem, 'title', languageEnabled) }}</h1>
      </div>
    </div>
    <div v-if="newsItem">
      <dynamic-tabs ref="dynamicTabsRef" :tabs="tabs" :active="openedTab" keep-alive @update="handleUpdate">
        <template #infos>
          <form-actions
            :onCancel="onCancel"
            :onSubmit="onSubmit"
            :languageErrors="languageErrors"
            :meta="meta"
            v-model:editLocale="editLocale"
            :languageEnabled="languageEnabled"
            :show-language-switcher="true"
          />
          <NewsItemInfo
            :languageEnabled="languageEnabled"
            :editLocale="editLocale"
            :module="newsItem"
            :theme-key="resolvedThemeKey"
            @save-image="handleSaveImage"
            @delete-image="handleClearImage"
          />
        </template>
      </dynamic-tabs>
    </div>
  </q-page>
</template>
<style lang="scss">
.module-detail {
  padding: 16px;

  .title {
    margin: 20px 0;
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    padding: 0 10px;
    gap: 10px;

    .q-btn {
      max-height: 36px;
    }

    div {
      display: flex;
      flex-direction: row;
      align-items: center;
      gap: 5px;
      max-width: 80%;

      h1 {
        display: block;
        max-width: 100%;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
      }

      .q-icon {
        font-size: $large-title;
      }
    }
  }

  .information-general {
    padding: 10px;

    &__header {
      display: flex;
      flex-direction: row;
      justify-content: space-between;
      padding: 10px 0;

      .title {
        color: $color-primary;
        font-size: $small-title;
      }
    }
  }
}
</style>
